import { ReactHTML } from 'react'
import { StyledConfig } from 'styled-components'

/**
 * There's a long-standing issue with styled-components (and similar CSS-in-JS libraries such as
 * Emotion) where extra styling props get forwarded to the DOM.
 *
 * @see https://styled-system.com/guides/removing-props-from-html/
 *
 * The tl;dr is that when you create an instance of styled.div with a "color" prop, the library
 * doesn't know whether it's something you want to handle as a React prop, or whether it should be
 * forwarded to the DOM as the "color" attribute.
 *
 * The set of attributes that pass styled-components' defaultValidatorFn() but are still known to be
 * unwanted is fairly small, so let's just list them here.
 */
const attributeBlacklist = new Set([
  'display', // https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/display
  'width',
  'height',
  'color',
  'fontVariant',
  'cursor',
  'transform',
  'overflow', // https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/overflow
])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const styledComponentConfig: StyledConfig<any> = {
  shouldForwardProp(prop, defaultValidatorFn) {
    if (prop === 'children') return true // this is a special case that needs to be allowed so that DOM nodes may have children
    if (!defaultValidatorFn(prop)) return false // this isn't a known valid HTML attribute -> no point forwarding it to the DOM
    if (attributeBlacklist.has(String(prop))) return false // this is a valid HTML attribute but we know we don't want it
    return true // by default, forward to the DOM as an attribute - whitelisting all valid attributes would be exhausting
  },
}

/**
 * Prop types for a styled-component of a given type; e.g. styled.div wouldn't accept a "href" prop,
 * but styled.a would.
 */
export type StyledComponentProps<HtmlTag extends keyof ReactHTML> = Parameters<
  ReactHTML[HtmlTag]
>[0]
